import type { DbLrpEndorsement } from '@harvestiq/iiq-common';
import z from 'zod';

export const NO_BFR_VFR_OPTION_VALUE = 'NONE';
export const VFR_OPTION_VALUE = 'VFR';
export const BFR_OPTION_VALUE = 'BFR';

export const bfrVfrOptionValueSchema = z.union([
  z.literal(NO_BFR_VFR_OPTION_VALUE),
  z.literal(VFR_OPTION_VALUE),
  z.literal(BFR_OPTION_VALUE),
  z.literal('1'),
  z.literal('2'),
  z.literal('3'),
  z.literal('4'),
  z.literal('5'),
  z.literal('6'),
  z.literal('7'),
  z.literal('8'),
  z.literal('9'),
  z.literal('10'),
]);

export type BfrVfrOptionValue = z.infer<typeof bfrVfrOptionValueSchema>;

export function getBfrVfrDiscountOptions({
  reinsuranceYear,
}: {
  reinsuranceYear?: number | null;
}) {
  // Default options available for all years
  const options: {
    label: string;
    value: BfrVfrOptionValue;
  }[] = [
    { label: 'None', value: NO_BFR_VFR_OPTION_VALUE },
    { label: 'Veteran Farmer/Rancher (VFR): 10%', value: VFR_OPTION_VALUE },
  ];

  // Display BFR option only for years before 2026
  if (reinsuranceYear && reinsuranceYear < 2026) {
    options.push({
      label: 'Beginning Farmer/Rancher (BFR): 10%',
      value: BFR_OPTION_VALUE,
    });
  }

  // Display BFR year options for 2026 and beyond
  else {
    const bfr2026Options = [
      { label: 'BFR Crop Year 1: 15%', value: '1' as const },
      { label: 'BFR Crop Year 2: 15%', value: '2' as const },
      { label: 'BFR Crop Year 3: 13%', value: '3' as const },
      { label: 'BFR Crop Year 4: 11%', value: '4' as const },
      { label: 'BFR Crop Year 5: 10%', value: '5' as const },
      { label: 'BFR Crop Year 6: 10%', value: '6' as const },
      { label: 'BFR Crop Year 7: 10%', value: '7' as const },
      { label: 'BFR Crop Year 8: 10%', value: '8' as const },
      { label: 'BFR Crop Year 9: 10%', value: '9' as const },
      { label: 'BFR Crop Year 10: 10%', value: '10' as const },
    ];
    options.push(...bfr2026Options);
  }

  return options;
}

/**
 * Converts the BFR/VFR fields from an endorsement into a single value that can be
 * used to set the BFR/VFR discount option in the UI.
 */
export function getBfrVfrOptionValue({
  isPre2026BeginningFarmerRancher,
  isVeteranFarmerRancher,
  beginningFarmerRancherCropYearCount,
}: Pick<
  DbLrpEndorsement,
  | 'isPre2026BeginningFarmerRancher'
  | 'isVeteranFarmerRancher'
  | 'beginningFarmerRancherCropYearCount'
>): BfrVfrOptionValue {
  // Handle 2026 BFR options first
  if (beginningFarmerRancherCropYearCount !== null) {
    return beginningFarmerRancherCropYearCount.toString() as BfrVfrOptionValue;
  }

  // Then handle pre-2026 BFR
  if (isPre2026BeginningFarmerRancher) {
    return BFR_OPTION_VALUE;
  }

  // Finally handle VFR
  if (isVeteranFarmerRancher) {
    return VFR_OPTION_VALUE;
  }

  return NO_BFR_VFR_OPTION_VALUE;
}

export function getEndorsementBfrVfrFields({
  bfrVfrDiscount,
}: {
  bfrVfrDiscount: BfrVfrOptionValue;
}): Pick<
  DbLrpEndorsement,
  | 'isPre2026BeginningFarmerRancher'
  | 'isVeteranFarmerRancher'
  | 'beginningFarmerRancherCropYearCount'
> {
  if (bfrVfrDiscount === VFR_OPTION_VALUE) {
    return {
      isPre2026BeginningFarmerRancher: false,
      isVeteranFarmerRancher: true,
      beginningFarmerRancherCropYearCount: null,
    };
  } else if (bfrVfrDiscount === BFR_OPTION_VALUE) {
    return {
      isPre2026BeginningFarmerRancher: true,
      isVeteranFarmerRancher: false,
      beginningFarmerRancherCropYearCount: null,
    };
  } else if (!isNaN(parseInt(bfrVfrDiscount, 10))) {
    // BFR year options
    return {
      isPre2026BeginningFarmerRancher: false,
      isVeteranFarmerRancher: false,
      beginningFarmerRancherCropYearCount: parseInt(bfrVfrDiscount, 10),
    };
  } else {
    // Default: NO_BFR_VFR_OPTION_VALUE
    return {
      isPre2026BeginningFarmerRancher: false,
      isVeteranFarmerRancher: false,
      beginningFarmerRancherCropYearCount: null,
    };
  }
}
